import React from 'react';
import Navigation from '../components/Navigation';
import Footer from '../components/Footer';
import SEO from '../components/SEO';
import { CheckCircle } from 'lucide-react';
import { useNavigate } from 'react-router-dom';

const GymPage = () => {
  const navigate = useNavigate();

  const membershipPackages = [
    {
      name: 'Monthly Membership',
      price: 'P550/month',
      features: ['Access to gym facilities', 'Group classes']
    },
    {
      name: '3 Months Membership',
      price: 'P1,650/3 months',
      features: ['Group classes', 'Unlimited classes', 'Access to gym facilities', 'Nutrition guidance']
    },
    {
      name: 'Student Membership',
      price: 'P350/month',
      features: ['Group classes', 'Competition preparation', 'Recovery sessions']
    },
    {
      name: 'Personal Training (PT)',
      price: 'From P200/session',
      features: ['Minimum contract term: 3 months', 'For Monthly Option Contact PT Trainer', 'Minimum 2 sessions per week']
    }
  ];

  const weeklySchedule = [
    {
      day: 'Monday',
      class: 'School Boxing',
      description: 'Boxing and Endurance',
      morning: '05:00 - 09:00',
      afternoon: '15:30 - 19:30'
    },
    {
      day: 'Tuesday',
      class: 'Below The Belt',
      description: 'Lower Body and Core Conditioning',
      morning: '05:00 - 09:00',
      afternoon: '15:30 - 19:30'
    },
    {
      day: 'Wednesday',
      class: 'Boxing HIIT',
      description: 'Explosive Boxing, Plyometrics and Heavy Bag',
      morning: '05:00 - 09:00',
      afternoon: '15:30 - 19:30'
    },
    {
      day: 'Thursday',
      class: 'The Challenger',
      description: 'Strength and Conditioning',
      morning: '05:00 - 09:00',
      afternoon: '15:30 - 19:30'
    },
    {
      day: 'Friday',
      class: 'Boxing Thriller',
      description: 'Relaxed Boxing Drills, Minimal Contact',
      morning: '05:00 - 09:00',
      afternoon: '15:30 - 19:30'
    },
    {
      day: 'Saturday',
      class: 'Body Blast',
      description: 'Full Body Boxing Workout',
      morning: '07:00 - 09:00'
    }
  ];

  return (
    <div className="min-h-screen bg-black">

      <SEO
        title="Boxing Gym in Gaborone | Bond Boxing"
        description="Train at Bond Boxing Gym in Gaborone, Botswana. Explore boxing classes, strength and conditioning, weekly training schedules and gym membership packages."
        path="/gym"
      />

      <Navigation />

      {/* Hero Section */}
      <section className="relative h-[60vh] flex items-center justify-center overflow-hidden">
        <div className="absolute inset-0 z-0">
          <img
            src="https://i.imgur.com/vdjRVed.jpeg"
            alt="Bond Boxing gym and training environment in Gaborone"
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-black/80 via-black/70 to-black"></div>
        </div>

        <div className="relative z-10 text-center px-6 max-w-4xl mx-auto">
          <h1 className="text-6xl md:text-7xl font-bold mb-6 tracking-wider">
            <span className="text-white">Classes</span>
          </h1>

          <p className="text-red-500 text-2xl md:text-3xl font-semibold mb-6 italic">
            "Train Like A Pro"
          </p>

          <p className="text-gray-300 text-lg md:text-xl max-w-2xl mx-auto">
            A fitness-focused training space built for strength, conditioning, and overall wellness. Workouts combine heavy bag sessions, cardio, and full-body training to help you stay active and consistent.
          </p>
        </div>
      </section>

      {/* About the Gym */}
      <section className="bg-zinc-950 py-20">
        <div className="container mx-auto px-6">
          <div className="grid md:grid-cols-2 gap-12 items-center">
            <div>
              <h2 className="text-red-600 text-4xl md:text-5xl font-bold mb-6">
                Inside the Gym
              </h2>

              <p className="text-gray-400 text-lg leading-relaxed mb-6">
                Bond Boxing Gym is a fitness gym in Gaborone built for anyone looking to level up their health and strength.
              </p>

              <p className="text-gray-400 text-lg leading-relaxed mb-6">
                With heavy bag workouts, conditioning sessions, and high-energy training, we help you stay active and push your limits. It’s not about fighting, it’s about getting stronger, fitter, and more confident every day.
              </p>

              <div className="grid grid-cols-2 gap-6 mt-8">
                <div className="flex items-start gap-3">
                  <span className="text-2xl">🏋️</span>
                  <div>
                    <div className="text-white font-bold text-2xl">
                      Strength & Conditioning
                    </div>
                  </div>
                </div>

                <div className="flex items-start gap-3">
                  <span className="text-2xl">⚡</span>
                  <div>
                    <div className="text-white font-bold text-2xl">
                      High-Energy Workouts
                    </div>
                  </div>
                </div>

                <div className="flex items-start gap-3">
                  <span className="text-2xl">🥊</span>
                  <div>
                    <div className="text-white font-bold text-2xl">
                      Boxing Fundamentals & Drills
                    </div>
                  </div>
                </div>

                <div className="flex items-start gap-3 col-span-2">
                  <span className="text-2xl">🧠</span>
                  <div>
                    <div className="text-white font-bold text-2xl">
                      Focused Training
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <div>
              <img
                src="https://i.imgur.com/q4GHpkB.png"
                alt="Boxing ring at Bond Boxing Gym"
                loading="lazy"
                decoding="async"
                className="w-full h-[500px] object-cover shadow-2xl"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Weekly Schedule */}
      <section className="bg-black py-20">
        <div className="container mx-auto px-6">
          <div className="text-center mb-12">
            <h2 className="text-white text-5xl md:text-6xl font-bold mb-6">
              Weekly Program
            </h2>

            <p className="text-gray-400 text-lg md:text-xl max-w-3xl mx-auto">
              Plan your training sessions with our comprehensive weekly program.
            </p>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6 max-w-7xl mx-auto">
            {weeklySchedule.map((session, index) => (
              <div
                key={index}
                className="bg-zinc-900 border border-red-600 rounded-2xl p-6 shadow-lg shadow-black/40 transition-transform duration-300 hover:-translate-y-1"
              >
                <div className="flex items-center justify-between mb-3">
                  <span className="text-red-500 text-xs uppercase tracking-wider font-bold">
                    {session.day}
                  </span>

                  <span className="text-white text-sm uppercase font-bold tracking-wide">
                    {session.class}
                  </span>
                </div>

                <p className="text-gray-300 text-sm leading-relaxed mb-4 font-semibold">
                  {session.description}
                </p>

                <div className="grid gap-2 text-white text-sm">
                  <p className="text-gray-300 font-semibold">
                    <span className="text-red-500">Morning:</span> {session.morning}
                  </p>

                  {session.afternoon && (
                    <p className="text-gray-300 font-semibold">
                      <span className="text-red-500">Afternoon:</span> {session.afternoon}
                    </p>
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Membership Packages */}
      <section id="membership" className="bg-black py-20">
        <div className="container mx-auto px-6">
          <div className="text-center mb-16">
            <h2 className="text-white text-5xl md:text-6xl font-bold mb-6">
              Membership Packages
            </h2>

            <p className="text-gray-400 text-lg md:text-xl max-w-3xl mx-auto">
              Choose the perfect membership plan that fits your training goals.
            </p>

            <p className="text-red-500 text-base md:text-lg font-medium mt-4">
              Walk-in fee is P75 and joining fee is P100.
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8 max-w-7xl mx-auto">
            {(Array.isArray(membershipPackages) ? membershipPackages : []).map((pkg, index) => (
              <div
                key={index}
                className={`bg-zinc-950 border-2 p-8 transition-all duration-300 hover:-translate-y-2 ${
                  index === 1
                    ? 'border-red-600 shadow-lg shadow-red-600/20'
                    : 'border-gray-800 hover:border-red-600'
                }`}
              >
                <h3 className="text-white text-2xl font-bold mb-4">
                  {pkg.name}
                </h3>

                <div className="text-red-500 text-4xl font-bold mb-6">
                  {pkg.price}
                </div>

                <ul className="space-y-4 mb-8">
                  {(Array.isArray(pkg.features) ? pkg.features : []).map((feature, idx) => (
                    <li key={idx} className="text-gray-400 flex items-start gap-2">
                      <CheckCircle size={20} className="text-red-500 flex-shrink-0 mt-0.5" />
                      <span>{feature}</span>
                    </li>
                  ))}
                </ul>

                <button
                  onClick={() => navigate(`/contact?plan=${encodeURIComponent(pkg.name)}`)}
                  className="w-full bg-red-600 hover:bg-red-700 text-white px-6 py-3 font-semibold transition-all duration-300"
                >
                  Choose Plan
                </button>
              </div>
            ))}
          </div>

          <div className="text-center mt-8">
            <p className="text-gray-500 text-sm">
              Need help picking a package? Talk to us in the contact section below.
            </p>
          </div>
        </div>
      </section>

      {/* Gym Facilities */}
      <section className="bg-zinc-950 py-20">
        <div className="container mx-auto px-6">
          <div className="text-center mb-16">
            <h2 className="text-white text-5xl md:text-6xl font-bold mb-6">
              Gym Facilities
            </h2>

            <p className="text-gray-400 text-lg">
              State-of-the-art equipment and training areas
            </p>
          </div>

          <div className="grid md:grid-cols-2 gap-8 max-w-4xl mx-auto">
            <div className="bg-black border border-gray-800 p-8 hover:border-red-600 transition-all duration-300 text-center">
              <div className="text-red-500 text-4xl mb-4">🥊</div>

              <h3 className="text-white text-xl font-bold mb-3">
                Heavy Bag Station
              </h3>

              <p className="text-gray-400">
                Multiple heavy bags for power training and conditioning workouts.
              </p>
            </div>

            <div className="bg-black border border-gray-800 p-8 hover:border-red-600 transition-all duration-300 text-center">
              <div className="text-red-500 text-4xl mb-4">💪</div>

              <h3 className="text-white text-xl font-bold mb-3">
                Strength & Conditioning
              </h3>

              <p className="text-gray-400">
                Complete weight room with free weights and strength equipment.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Join CTA */}
      <section id="join" className="bg-black py-20">
        <div className="container mx-auto px-6">
          <div className="max-w-4xl mx-auto text-center">
            <h2 className="text-white text-5xl md:text-6xl font-bold mb-6">
              Ready to Start?
            </h2>

            <p className="text-gray-400 text-lg md:text-xl mb-10">
              Join Bond Boxing Gym today and begin your transformation journey.
            </p>

            <button
              onClick={() => navigate('/contact')}
              className="bg-red-600 hover:bg-red-700 text-white px-12 py-5 font-semibold text-lg tracking-wide transition-all duration-300 hover:scale-105 hover:shadow-lg hover:shadow-red-600/50"
            >
              Join The Gym
            </button>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default GymPage;